<template>
	<div
		class="overlay-childproducts"
		:class="{
			'overlay-childproducts--show-summary': showSummary,
		}"
		v-if="visible"
		v-htmlclass="customHtmlClasses"
	>
		<button
			class="overlay-childproducts__close"
			@click.prevent="close"
		>
			<SVGClose/>
		</button>

		<div class="overlay-childproducts__wrapper">
			<div class="overlay-childproducts__content">
				<div
					v-for="[type, categories] in Object.entries(childProductCategoriesGrouped)"
					:key="type"
					class="overlay-childproducts__content-block"
				>
					<h1
						v-if="categories.length"
						class="overlay-childproducts__title"
						:class="[
							`overlay-childproducts__title--${type}`
						]"
						v-html="stepTitle[type]"
					/>
					<div
						v-if="stepDescription[type]"
						class="overlay-childproducts__description"
						v-sanitize="stepDescription[type]"
					/>
					<Collapsible
						v-for="(category, index) in categories"
						:key="category.id"
						class="overlay-childproducts__category"
						initial-open
					>
						<template slot="trigger">
							<h3
								class="overlay-childproducts__category-title"
							>
								<span v-html="category.title"/>
								<small v-html="categoryHint(category)"/>
							</h3>
							<div class="overlay-childproducts__category-preview">
								<ul
									class="overlay-childproducts__category-preview-list"
								>
									<li
										v-for="child in categoryPreview(category)"
										:key="child.code"
										class="overlay-childproducts__category-preview-item"
									>
										<img
											v-if="child.imageMood || child.image"
											class="overlay-childproducts__category-preview-item-image"
											:class="{
												'overlay-childproducts__category-preview-item-image--wide': child.imageMood
											}"
											:src="child.imageMood || child.image"
											:alt="child.shortDescription"
										>
										<span
											class="overlay-childproducts__category-preview-item-name"
											v-html="child.name"
										/>
									</li>
								</ul>
							</div>
							<div
								class="overlay-childproducts__category-error"
								v-html="errors[category.taxon]"
								v-if="errors[category.taxon]"
							/>
						</template>
						<ul
							class="overlay-childproducts__items"
						>
							<li
								v-for="child in category.products"
								:key="child.code"
								class="overlay-childproducts__item"
								:class="{
									'overlay-childproducts__item--checked': selectedMap[child.code]
								}"
							>
								<label
									class="overlay-childproducts__item-label"
								>
									<input
										type="checkbox"
										class="overlay-childproducts__item-input"
										:name="category.taxon"
										:value="child.code"
										:checked="selectedMap[child.code]"
										immediate
										@change="onItemChange(child, category)"
									>
									<figure
										class="overlay-childproducts__item-image"
										v-if="child.imageMood || child.image"
									>
										<img
											class="overlay-childproducts__item-image-element"
											:class="{
												'overlay-childproducts__item-image-element--wide': child.imageMood
											}"
											:src="child.imageMood || child.image"
											:alt="child.shortDescription"
											loading="lazy"
										>
									</figure>
									<div class="overlay-childproducts__item-content">
										<span
											class="overlay-childproducts__item-name"
											v-html="child.name"
										/>
										<div class="overlay-childproducts__item-price">
											<strike
												v-if="child.originalPrice > child.price"
												class="overlay-childproducts__item-price-old"
												v-html="formatItemPrice(child.originalPrice)"
											/>
											<span
												class="overlay-childproducts__item-price-current"
												v-html="formatItemPrice(child.price)"
											/>
										</div>
										<div
											class="overlay-childproducts__item-price-reduction"
											v-if="priceReduction(child)"
											v-html="priceReduction(child)"
										/>
									</div>
								</label>
							</li>
						</ul>
					</Collapsible>
				</div>

				<div
					class="overlay-childproducts__empty"
					v-if="!options.length && !extras.length"
					v-html="$t('childproducts.noOptions')"
				/>
			</div>

			<div class="overlay-childproducts__summary">
				<div class="overlay-childproducts__summary-content">
					<div class="overlay-childproducts__summary-product">
						<div class="overlay-childproducts__summary-product-title">
							<h3 class="overlay-childproducts__summary-product-name" v-html="item.name" />
							<span class="overlay-childproducts__summary-product-price" v-html="$options.filters.fwgpricecent(item.price, 'short')" />
						</div>
						<div class="overlay-childproducts__summary-product-options" v-html="variantName" v-if="variantName" />
					</div>
				</div>
				<div class="overlay-childproducts__summary-content overlay-childproducts__summary-scroll">
					<template v-for="(category, index) in selectedItemsByCategory">
						<h2
							class="overlay-childproducts__summary-title"
							v-html="category.title"
						/>
						<ul class="overlay-childproducts__summary-extras">
							<li
								class="overlay-childproducts__summary-extra"
								v-for="extra in category.products"
								:key="`summary-extra-${extra.code}`"
							>
								<span class="overlay-childproducts__summary-extra-name" v-html="extra.name" />
								<span class="overlay-childproducts__summary-extra-price" v-html="formatItemPrice(extra.price)" />
							</li>
						</ul>
					</template>
				</div>
				<div class="overlay-childproducts__summary-content overlay-childproducts__summary-total">
					<span class="overlay-childproducts__summary-total-title" v-html="$t('childproducts.total')" />
					<span class="overlay-childproducts__summary-total-price" v-html="$options.filters.fwgpricecent(total, 'short')" />
				</div>
				<Button
					class="overlay-childproducts__summary-button overlay-childproducts__summary-button--desktop"
					@click.prevent="onButtonClick()"
					level="primary"
					type="button"
					:text="buttonText"
				/>
				<Button
					class="overlay-childproducts__summary-button overlay-childproducts__summary-button--mobile"
					@click.prevent="onCloseSummary()"
					level="primary"
					type="button"
					:text="$t('childproducts.backButton')"
				/>
			</div>

			<div class="overlay-childproducts__sticky">
				<div
					class="overlay-childproducts__sticky-details"
					@click.prevent="onOpenSummary()"
				>
					<Button
						class="overlay-childproducts__sticky-details-button"
						type="button"
						level="tertiary"
						:text="countButtonText"
						arrow
					/>
					<div
						class="overlay-childproducts__sticky-details-total"
						v-html="$options.filters.fwgpricecent(total)"
					/>
				</div>
				<Button
					class="overlay-childproducts__sticky-button"
					@click.prevent="onButtonClick()"
					level="primary"
					type="button"
					:text="buttonText"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex'
import htmlComponent from 'htmlcomponent'
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';
import Button from '@/components/Button'
import FormInputCheckbox from "@/components/FormInputCheckbox"
import Collapsible from "@/components/Collapsible"
import {storeScrollPos, restoreScrollPos} from '@/utils/scrollpos';

export default {
	mixins: [
		GenericMixin,
		OverlayMixin,
	],
	components: {
		Button,
		FormInputCheckbox,
		Collapsible,
	},
	props: {
		overlayType: {
			type: String,
			default() {
				return 'overlay-childproducts';
			},
		},
		initialVisible: {
			type: Boolean,
			default: false
		},
		htmlClasses: {
			type: Array,
			default: () => ['hide--header', 'show--modal'],
		},
	},
	data() {
		return {
			errors: {},
			showSummary: false,
			item: null,
			selectedCodes: [],
			newItem: false,
			selectedMap: {},
			visible: this.initialVisible
		}
	},
	computed: {
		...mapGetters("childproducts", [
			"childProductsByType",
		]),
		variantName() {
			return this.item.variant != this.item.name && this.item.variant;
		},
		stepTitle() {
			return {
				options: this.$t(`childproducts.stepTitleOptions`),
				extras: this.$t(`childproducts.stepTitleExtras`),
			}
		},
		stepDescription() {
			return {
				extras: this.item?.additionalTextExtras || null,
			}
		},
		childProductTypes() {
			return Object.keys(this.childProductsByType).reduce((acc, type) => {
				acc[type] = this.childProductsByType[type].map(category => {
					return {
						...category,
						products: category.products
							.filter(product => this.item.availableChildProducts.includes(product.code))
					};
				}).filter(category => category.products.length);
				return acc;
			}, {});
		},
		childProductCategories() {
			return [
				...this.childProductTypes?.option,
				...this.childProductTypes?.extra,
			]
		},
		childProductCategoriesGrouped() {
			const categories = {}

			if (this.options.length) {
				categories.options = this.options;
			}

			if (this.extras.length) {
				categories.extras = this.extras;
			}

			return categories;
		},
		options() {
			return [
				...this.childProductTypes?.option,
				...this.childProductTypes?.extra.filter(e => e.selectMin)
			];
		},
		extras() {
			return [
				...this.childProductTypes?.extra.filter(e => !e.selectMin)
			];
		},
		selectedItemsByCategory() {
			return this.childProductCategories.map(category => {
				return {
					...category,
					products: category.products.filter(p => this.selectedMap[p.code])
				}
			}).filter(category => category.products.length);
		},
		selectedItems() {
			return this.childProductCategories.reduce((acc, category) => {
				return [
					...acc,
					...category.products.filter(p => this.selectedMap[p.code]),
				];
			}, []);
		},
		itemsTotal() {
			return this.selectedItems.reduce((acc, item) => acc + item.price, 0);
		},
		total() {
			return this.item.price + this.itemsTotal;
		},
		customHtmlClasses() {
			return this.htmlClasses.reduce((acc, cls) => {
				return {...acc, [cls]: this.visible};
			}, {});
		},
		buttonText() {
			return this.newItem ? this.$t('childproducts.buyButton') : this.$t('childproducts.editButton');
		},
		countButtonText() {
			return this.$tc('childproducts.optionsCount', this.selectedItems.length, { count: this.selectedItems.length })
		}
	},
	watch: {
		initialVisible(newValue) {
			this.visible = newValue;
		},
	},
	methods: {
		priceReduction(product) {
			if (product.originalPrice > product.price) {
				if (product.discountInPercent) {
					return `-${Math.floor(100 / product.originalPrice * (product.originalPrice - product.price))}%`;
				}
				else {
					return `-${this.$options.filters.fwgpricecent((product.originalPrice - product.price), 'short')} CHF`;
				}
			}
			return false;
		},
		categoryPreview(category) {
			const c = this.selectedItemsByCategory?.find(c => c.id === category.id)
			return c?.products
		},
		onItemChange(item, category) {
			let selectedMap = {...this.selectedMap};
			const products = category.products.map(product => product.code);
			const count = products.reduce((acc, product) => {
				return selectedMap[product] ? ++acc : acc;
			}, 0);
			const max = category.selectMax;
			const min = category.selectMin;
			let error = '';

			if (selectedMap[item.code]) {
				if (!min || count > 1 || (min && min < max)) {
					selectedMap[item.code] = false;
				}
			}
			else {
				if (max == 1) {
					selectedMap = {
						...selectedMap,
						...products.reduce((acc, product) => {
							return {
								...acc,
								[product]: item.code == product,
							};
						}, {}),
					};
				}
				else if (!max || count < max) {
					selectedMap[item.code] = true;
				}
				else {
					error = this.$t('childproducts.errors.max', { max, over: 1 });
				}
			}

			Vue.set(this.errors, category.taxon, error);
			Vue.set(this, 'selectedMap', selectedMap);
		},
		validateStep() {
			let selectedMap = {...this.selectedMap};
			let error = this.childProductCategories.reduce((acc, category) => {
				const products = category.products.map(product => product.code);
				const count = products.reduce((acc, product) => {
					return selectedMap[product] ? ++acc : acc;
				}, 0);
				const max = category.selectMax;
				const min = category.selectMin;
				let error = '';


				if (min && count < min) {
					acc = true;
					error = this.$t('childproducts.errors.min', { min });
				}
				else if (max && count > max) {
					acc = true;
					error = this.$t('childproducts.errors.max', { max, over: count - max });
				}

				Vue.set(this.errors, category.taxon, error);
				return acc;
			}, false);

			return error;
		},
		onButtonClick() {
			const error = this.validateStep();

			if (error) {
				return;
			}

			this.onEdit();
		},
		onEdit() {
			let childCodes = Object.entries(this.selectedMap)
				.reduce((p, [k, v]) => {
					if (v === true) {
						p.push(k)
					}
					return p
				}, [])

			if (this.newItem) {
				this.$emit('add', this.item, childCodes)
			}
			else {
				this.$emit('edit', this.item, childCodes)
			}
		},
		onClose() {
			this.$emit('close')
		},

		onOpenSummary() {
			this.showSummary = true;
		},

		onCloseSummary() {
			this.showSummary = false;
		},

		categoryHint(category) {
			const max = category.selectMax;
			const min = category.selectMin;
			let hint = '';

			if (min && max) {
				if (min == max) {
					hint = this.$t('childproducts.hints.exact', { min, max });
				}
				else {
					hint = this.$t('childproducts.hints.range', { min, max });
				}
			}
			else if (min) {
				hint = this.$t('childproducts.hints.min', { min, max });
			}
			else if (max) {
				hint = this.$t('childproducts.hints.max', { min, max });
			}

			return hint;
		},

		formatItemPrice(price) {
			return `${price >= 0 ? '+' : '-'}&nbsp;${this.$options.filters.fwgpricecent(Math.abs(price), 'short')}`
		},

		open() {
			this.visible = true;
			this.openOverlay();
			this.$emit('open');
		},
		close() {
			this.visible = false;
			this.closeOverlay();
			this.$emit('close');
		},

		openOverlayHandler(e, data)  {
			if (data.type !== this.overlayType) {
				this.html.classList.remove(this.overlayShowClass);
				this.visible = false;
			}
			else {
				if (!this.overlayIgnoreScrollPos) {
					storeScrollPos();
				}

				this.item = data.item || data.product;
				this.newItem = !!data.product;
				this.selectedCodes = data.selectedCodes || [];
				this.selectedMap = [
					...this.selectedCodes,
				].reduce((acc, code) => {
					return {...acc, [code]: true}
				}, {});

				this.html.classList.add(this.overlayShowClass);
				this.visible = true;
			}
		},
		closeOverlayHandler(e, data) {
			if (data.type === this.overlayType) {
				this.html.classList.remove(this.overlayShowClass);
				this.visible = false;
				this.showSummary = false;
				this.errors = {};

				if (!this.overlayIgnoreScrollPos) {
					restoreScrollPos();
				}
			}
		},
		toggleOverlayHandler(e, data) {
			if (data.type === this.overlayType) {
				if (this.html.classList.contains(this.overlayShowClass)) {
					htmlComponent.send('overlay:close', {type: this.overlayType});
				}
				else {
					htmlComponent.send('overlay:open', {type: this.overlayType});
				}
			}
		},
	},
}
</script>

<style>

</style>
